import { isChatStatusBusy, useStores } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { useId } from 'react';

import { Icons } from '@/assets/icons/icons';
import { HotkeyLabel } from '@/components/blocks/Hotkey';
import { Button, IconButton, XStack, YStack } from '@/components/ui';
import { chatStore, type QueuedChatMessage } from '@/features/chat/chat.store';
import type { SendNowAction } from '@/features/chat/queued-messages';

import { modEnterSends } from './useRegisterSendChat';

type QueuedMessagesProps = {
  threadId: string;
  /** What the first queued message's button does; no button without it */
  sendNowAction?: SendNowAction;
  isSendingNow: boolean;
  onSendNow: () => void;
  onRemove: (message: QueuedChatMessage) => void;
};

const getQueuedMessageSummary = (message: QueuedChatMessage): string => {
  if (message.content.trim()) return message.content;
  if (message.selectedTextCount > 0) {
    return message.selectedTextCount === 1
      ? 'Selected text'
      : `${message.selectedTextCount} selected texts`;
  }
  if (message.attachmentCount > 0) {
    return message.attachmentCount === 1
      ? '1 attachment'
      : `${message.attachmentCount} attachments`;
  }
  return "Answers to the agent's questions";
};

const STEER_UNAVAILABLE_DESCRIPTION =
  "A message with mentions, answers to the agent's questions or a command can't steer the response. It sends when the response ends.";

const SEND_NOW_BUTTONS: Record<
  SendNowAction,
  { label: string; ariaLabel: string; loadingText?: string; tooltip: string }
> = {
  interrupt: {
    label: 'Interrupt',
    ariaLabel: 'Interrupt and send next queued message',
    loadingText: 'Interrupting…',
    tooltip: 'Interrupt the response and send this message',
  },
  send: {
    label: 'Send',
    ariaLabel: 'Send next queued message',
    loadingText: 'Sending…',
    tooltip: 'Send this message',
  },
  steer: {
    label: 'Steer',
    ariaLabel: 'Steer the response with next queued message',
    loadingText: 'Steering…',
    tooltip: 'Steer the response with this message without stopping it',
  },
  steer_unavailable: {
    label: 'Steer',
    ariaLabel: 'Steer the response with next queued message',
    tooltip: STEER_UNAVAILABLE_DESCRIPTION,
  },
};

export const QueuedMessages = observer(
  ({
    threadId,
    sendNowAction,
    isSendingNow,
    onSendNow,
    onRemove,
  }: QueuedMessagesProps) => {
    const sendNowDescriptionId = useId();
    const { messages: messagesStore, threads: threadsStore } = useStores();
    const messages = chatStore.queuedMessagesByThread.get(threadId) ?? [];
    // A steered message shows here only until the chat has it
    const steeredMessages = (
      chatStore.steeredMessagesByThread.get(threadId) ?? []
    ).filter(({ id }) => !messagesStore.data.has(id));
    if (messages.length === 0 && steeredMessages.length === 0) return null;
    // A steered message stays after the response ends, until the chat has it or it goes back to the queue
    const steeredLabel = isChatStatusBusy(
      threadsStore.getById(threadId)?.status
    )
      ? 'Steering the response'
      : 'Adding to the chat';
    const sendNowButton = sendNowAction && SEND_NOW_BUTTONS[sendNowAction];

    return (
      <YStack
        aria-label="Queued messages"
        className="mb-2 max-h-40 gap-1.5 overflow-y-auto scrollbar-hide"
        role="list"
      >
        {steeredMessages.map((message) => (
          <YStack
            key={message.id}
            className="shrink-0 gap-0.5 rounded-xl border border-border bg-surface-hover px-3 py-2"
            role="listitem"
          >
            <span className="text-xs text-foreground-subtle">
              {steeredLabel}
            </span>
            <span className="line-clamp-2 text-sm leading-relaxed text-foreground">
              {getQueuedMessageSummary(message)}
            </span>
          </YStack>
        ))}
        {messages.map((message, index) => (
          <YStack
            key={message.id}
            className="shrink-0 gap-0.5 rounded-xl border border-border bg-surface-hover px-3 py-2"
            role="listitem"
          >
            <XStack className="items-center gap-1">
              <span className="flex-1 text-xs text-foreground-subtle">
                Queued {index + 1} of {messages.length}
                {message.steerOutcomeUnknown
                  ? ' · may not have steered the response'
                  : null}
              </span>
              {index === 0 && sendNowAction === 'steer_unavailable' ? (
                // Not `disabled`, so keyboard and screen-reader users can reach it and hear why;
                // `hover:enabled:bg-secondary` cancels the hover style
                <>
                  <Button
                    aria-describedby={sendNowDescriptionId}
                    aria-disabled
                    aria-label={SEND_NOW_BUTTONS.steer_unavailable.ariaLabel}
                    className="cursor-not-allowed opacity-50 hover:enabled:bg-secondary"
                    size="xs"
                    tooltip={SEND_NOW_BUTTONS.steer_unavailable.tooltip}
                    type="button"
                    variant="secondary"
                  >
                    {SEND_NOW_BUTTONS.steer_unavailable.label}
                  </Button>
                  <span className="sr-only" id={sendNowDescriptionId}>
                    {SEND_NOW_BUTTONS.steer_unavailable.tooltip}
                  </span>
                </>
              ) : index === 0 && sendNowButton ? (
                <Button
                  aria-label={sendNowButton.ariaLabel}
                  isLoading={isSendingNow}
                  loadingText={sendNowButton.loadingText}
                  onClick={onSendNow}
                  size="xs"
                  tooltip={sendNowButton.tooltip}
                  tooltipOptions={{
                    hotkey: modEnterSends()
                      ? ['mod', 'alt', 'enter']
                      : ['mod', 'enter'],
                  }}
                  type="button"
                  variant="secondary"
                >
                  {sendNowButton.label}
                  <HotkeyLabel keyName="mod" size="xs" />
                  {modEnterSends() ? (
                    <HotkeyLabel keyName="alt" size="xs" />
                  ) : null}
                  <HotkeyLabel keyName="enter" size="xs" />
                </Button>
              ) : null}
              <IconButton
                aria-label={`Remove queued message ${index + 1}`}
                className="shrink-0"
                disabled={index === 0 && isSendingNow}
                icon={Icons.cancel}
                onClick={() => onRemove(message)}
                size="xs"
                tooltip="Remove from queue"
                type="button"
                variant="ghost"
              />
            </XStack>
            <span className="line-clamp-2 text-sm leading-relaxed text-foreground">
              {getQueuedMessageSummary(message)}
            </span>
          </YStack>
        ))}
      </YStack>
    );
  }
);
